import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowRight,
  FiLayers,
  FiShield,
  FiTrendingUp,
} from 'react-icons/fi';
import { useLanguage } from '../components';
import ComplianceNotice from '../components/ComplianceNotice';
import './ConceptsLibrary.css';

const UI_COPY = {
  en: {
    title: 'Financial Concepts Library',
    subtitle:
      'Learn one idea at a time with practical examples, common mistakes, and next actions.',
    stepTrack: 'Step 1',
    stepConcept: 'Step 2',
    stepDetails: 'Step 3',
    trackTitle: 'What do you want to understand first?',
    trackSubtitle: 'Start with one learning path, then pick the concept that matters most right now.',
    labels: {
      trackList: 'Choose a learning path',
      selectedTrack: 'Selected path',
      conceptList: 'Choose a concept',
      conceptPrompt: 'Only concepts linked to this path are shown.',
      simple: 'Simple explanation',
      visual: 'Visual example',
      realLife: 'Real-life example',
      misconception: 'Common misconception',
      nextMove: 'Practical next move',
      checklist: 'Quick checklist',
      practicalGuide: 'How this works in real life',
      concreteExample: 'Concrete example',
      conceptChart: 'Quick visual chart',
    },
    detailTitle: 'Concept details',
    detailSubtitle: 'Review the quick summary first, then continue through practical explanations and examples.',
    quickSummaryTitle: 'Quick summary first',
    quickSummarySubtitle: 'One-screen orientation before the deeper details.',
    quickOneThing: 'If you remember one thing',
    quickWhyNow: 'Why this matters now',
    quickTrap: 'Most common trap',
    backToStep1: 'Back to Step 1',
    backToStep2: 'Back to Step 2',
    changePath: 'Change learning path',
    changeConcept: 'Change concept',
    nextSectionTitle: 'Where to apply this next',
    nextSectionSubtitle: 'Move directly to planning tools that use this concept.',
    nextLinks: {
      accounts: 'Open Learn Accounts',
      forecast: 'Open Growth Forecast',
    },
  },
  fr: {
    title: 'Bibliothèque des concepts financiers',
    subtitle:
      'Apprenez un concept à la fois avec des exemples concrets, des erreurs fréquentes et des actions pratiques.',
    stepTrack: 'Étape 1',
    stepConcept: 'Étape 2',
    stepDetails: 'Étape 3',
    trackTitle: 'Que voulez-vous comprendre en premier ?',
    trackSubtitle: 'Commencez par un parcours, puis choisissez le concept le plus utile maintenant.',
    labels: {
      trackList: 'Choisissez un parcours',
      selectedTrack: 'Parcours sélectionné',
      conceptList: 'Choisissez un concept',
      conceptPrompt: 'Seuls les concepts liés à ce parcours sont affichés.',
      simple: 'Explication simple',
      visual: 'Exemple visuel',
      realLife: 'Exemple concret',
      misconception: 'Idée fausse courante',
      nextMove: 'Prochaine action concrète',
      checklist: 'Mini liste de contrôle',
      practicalGuide: 'Comment cela fonctionne dans la vraie vie',
      concreteExample: 'Exemple concret',
      conceptChart: 'Mini graphique visuel',
    },
    detailTitle: 'Détails du concept',
    detailSubtitle: 'Lisez d’abord le résumé, puis suivez les explications pratiques et les exemples.',
    quickSummaryTitle: 'Résumé rapide en premier',
    quickSummarySubtitle: 'Une vue d’ensemble en un écran avant les détails.',
    quickOneThing: 'À retenir absolument',
    quickWhyNow: 'Pourquoi c’est important maintenant',
    quickTrap: 'Piège le plus fréquent',
    backToStep1: 'Retour à l’étape 1',
    backToStep2: 'Retour à l’étape 2',
    changePath: 'Changer de parcours',
    changeConcept: 'Changer de concept',
    nextSectionTitle: 'Où appliquer cela ensuite',
    nextSectionSubtitle: 'Passez directement aux outils de planification liés à ce concept.',
    nextLinks: {
      accounts: 'Ouvrir Apprendre les comptes',
      forecast: 'Ouvrir Projection de croissance',
    },
  },
};

const LEARNING_TRACKS = [
  {
    id: 'growth',
    icon: FiTrendingUp,
    conceptIds: ['compound', 'inflation', 'dca'],
    en: {
      title: 'Growing long-term savings',
      description: 'Compounding, inflation, and consistent investing habits.',
    },
    fr: {
      title: 'Faire croître l’épargne à long terme',
      description: 'Capitalisation, inflation et habitudes de cotisation régulière.',
    },
  },
  {
    id: 'risk',
    icon: FiLayers,
    conceptIds: ['risk-return', 'diversification', 'allocation'],
    en: {
      title: 'Building a resilient portfolio',
      description: 'Risk tradeoffs, diversification, and asset mix decisions.',
    },
    fr: {
      title: 'Construire un portefeuille résilient',
      description: 'Compromis risque/rendement, diversification et répartition.',
    },
  },
  {
    id: 'tax',
    icon: FiShield,
    conceptIds: ['tax-efficiency', 'registered-vs-non'],
    en: {
      title: 'Improving after-tax outcomes',
      description: 'Account choice and tax-aware planning basics.',
    },
    fr: {
      title: 'Améliorer le résultat après impôt',
      description: 'Choix du compte et bases de la planification fiscale.',
    },
  },
];

const CONCEPT_LIBRARY = {
  compound: {
    en: {
      name: 'Compound Interest',
      hook: 'Small contributions can accelerate when given enough time.',
      simple: 'Your money can earn returns, and those returns can also earn returns over time.',
      visual: 'Think of a snowball: growth starts small, then increases as the base gets larger.',
      realLife: 'Saving 200 per month over many years can produce growth greater than total contributions alone.',
      myth: 'Misconception: Compounding only helps people who start with a large initial amount.',
      oneThing: 'Time in the market is often more powerful than trying to time the market.',
      whyNow: 'Starting earlier with smaller amounts usually beats waiting for a perfect moment.',
      nextMove: 'Automate a monthly contribution and raise it slightly whenever your income increases.',
      checklist: [
        'Choose one automatic contribution amount you can sustain for 12 months.',
        'Review and increase that contribution once per year.',
      ],
    },
    fr: {
      name: 'Intérêt composé',
      hook: 'De petites cotisations peuvent accélérer avec suffisamment de temps.',
      simple: 'Votre argent peut produire des rendements, puis ces rendements produisent à leur tour des rendements.',
      visual: 'Imaginez une boule de neige : la croissance commence doucement puis s’accélère.',
      realLife: 'Épargner 200 par mois pendant plusieurs années peut produire une croissance supérieure aux cotisations totales.',
      myth: 'Idée fausse : la capitalisation aide seulement les personnes qui commencent avec un gros montant.',
      oneThing: 'La durée d’investissement compte souvent plus que le timing parfait.',
      whyNow: 'Commencer tôt avec un petit montant est souvent meilleur que d’attendre le bon moment.',
      nextMove: 'Automatisez une cotisation mensuelle et augmentez-la légèrement quand votre revenu progresse.',
      checklist: [
        'Choisissez un montant automatique que vous pouvez maintenir pendant 12 mois.',
        'Révisez puis augmentez ce montant une fois par an.',
      ],
    },
  },
  inflation: {
    en: {
      name: 'Inflation',
      hook: 'Cash loses buying power when prices trend upward.',
      simple: 'Prices usually rise over time, reducing what each dollar can buy.',
      visual: 'A monthly budget that works now may purchase less in 10 to 20 years.',
      realLife: 'Retirement or education targets should include inflation, not just today’s costs.',
      myth: 'Misconception: Holding everything in cash is always the safest long-term plan.',
      oneThing: 'Plan future goals in tomorrow’s dollars, not today’s dollars.',
      whyNow: 'Ignoring inflation can make long-term plans look funded when they are not.',
      nextMove: 'When setting goal targets, add an inflation assumption and revisit it annually.',
      checklist: [
        'Use an inflation assumption for all goals longer than 3 years.',
        'Re-estimate major goals at least once each year.',
      ],
    },
    fr: {
      name: 'Inflation',
      hook: 'Le pouvoir d’achat du cash baisse quand les prix montent.',
      simple: 'Les prix augmentent généralement avec le temps, ce qui réduit ce qu’un dollar peut acheter.',
      visual: 'Un budget mensuel suffisant aujourd’hui peut être insuffisant dans 10 à 20 ans.',
      realLife: 'Les objectifs de retraite ou d’études doivent intégrer l’inflation, pas seulement les coûts actuels.',
      myth: 'Idée fausse : garder tout en cash est toujours la stratégie la plus sûre à long terme.',
      oneThing: 'Planifiez les objectifs futurs en dollars de demain, pas en dollars d’aujourd’hui.',
      whyNow: 'Ignorer l’inflation peut donner une impression trompeuse de préparation.',
      nextMove: 'Ajoutez une hypothèse d’inflation à chaque objectif long terme et réévaluez-la chaque année.',
      checklist: [
        'Utilisez une hypothèse d’inflation pour tout objectif de plus de 3 ans.',
        'Réestimez les grands objectifs au moins une fois par an.',
      ],
    },
  },
  'risk-return': {
    en: {
      name: 'Risk vs Return',
      hook: 'Higher expected returns usually come with wider swings.',
      simple: 'Higher expected return usually means more uncertainty and larger portfolio fluctuations.',
      visual: 'Conservative portfolios are steadier; growth portfolios can rise more but also fall harder.',
      realLife: 'If volatility pushes you to panic-sell, your plan likely carries more risk than you can tolerate.',
      myth: 'Misconception: The highest expected return option is always the best choice.',
      oneThing: 'The best plan is one you can stay invested in during bad markets.',
      whyNow: 'Choosing too much risk often causes emotional decisions that harm long-term outcomes.',
      nextMove: 'Stress-test your plan with a market drop scenario and check how you would react.',
      checklist: [
        'Define a maximum drawdown you can emotionally handle.',
        'Use that limit when selecting your risk profile.',
      ],
    },
    fr: {
      name: 'Risque et rendement',
      hook: 'Un rendement attendu plus élevé implique souvent des variations plus fortes.',
      simple: 'Un rendement attendu plus élevé s’accompagne généralement de plus d’incertitude.',
      visual: 'Un portefeuille prudent est plus stable; un portefeuille de croissance fluctue davantage.',
      realLife: 'Si la volatilité vous pousse à vendre en panique, le niveau de risque est probablement trop élevé.',
      myth: 'Idée fausse : l’option au rendement attendu le plus élevé est toujours la meilleure.',
      oneThing: 'Le meilleur plan est celui que vous pouvez conserver même pendant les baisses.',
      whyNow: 'Un risque mal calibré déclenche souvent des décisions émotionnelles coûteuses.',
      nextMove: 'Testez votre plan avec un scénario de baisse de marché et observez votre réaction.',
      checklist: [
        'Définissez une baisse maximale que vous pouvez tolérer psychologiquement.',
        'Utilisez cette limite pour choisir votre profil de risque.',
      ],
    },
  },
  diversification: {
    en: {
      name: 'Diversification',
      hook: 'Spreading exposure can reduce single-position damage.',
      simple: 'Diversification reduces concentration risk by spreading exposure across different assets and sectors.',
      visual: 'A mixed portfolio often moves more smoothly than a portfolio concentrated in one company or theme.',
      realLife: 'Owning only one stock can create large losses if that business has company-specific issues.',
      myth: 'Misconception: Holding many tickers always means true diversification.',
      oneThing: 'Diversification is about different risk drivers, not just a higher number of holdings.',
      whyNow: 'Concentration risk can undo years of progress in a short period.',
      nextMove: 'Check whether your largest positions are tied to the same sector or economic driver.',
      checklist: [
        'Review top 5 positions and their combined weight monthly.',
        'Set a maximum concentration limit per position or sector.',
      ],
    },
    fr: {
      name: 'Diversification',
      hook: 'Répartir l’exposition réduit le risque d’une position unique.',
      simple: 'La diversification limite le risque de concentration en répartissant l’exposition.',
      visual: 'Un portefeuille varié évolue souvent plus régulièrement qu’un portefeuille concentré.',
      realLife: 'Détenir une seule action peut créer de fortes pertes en cas de problème propre à l’entreprise.',
      myth: 'Idée fausse : avoir beaucoup de titres signifie toujours une vraie diversification.',
      oneThing: 'Diversifier, c’est répartir les sources de risque, pas seulement multiplier les lignes.',
      whyNow: 'Un risque de concentration peut effacer rapidement des années de progression.',
      nextMove: 'Vérifiez si vos plus grosses positions dépendent du même secteur ou moteur économique.',
      checklist: [
        'Examinez chaque mois le poids cumulé des 5 plus grosses positions.',
        'Fixez une limite maximale par position ou par secteur.',
      ],
    },
  },
  allocation: {
    en: {
      name: 'Asset Allocation',
      hook: 'Your asset mix usually drives long-term portfolio behavior.',
      simple: 'Asset allocation is how you split money across equities, fixed income, and cash-like assets.',
      visual: 'Different mixes produce different tradeoffs between growth potential and short-term stability.',
      realLife: 'A short-term home goal often needs more stability than a long-term retirement goal.',
      myth: 'Misconception: Security selection matters more than allocation for long-term outcomes.',
      oneThing: 'Match your allocation to your goal timeline before selecting individual investments.',
      whyNow: 'Good allocation decisions reduce the chance of forced selling near a goal date.',
      nextMove: 'Assign each major goal a timeline, then choose a suitable risk level for that timeline.',
      checklist: [
        'Separate short-term and long-term goals before investing.',
        'Review allocation annually or after major life changes.',
      ],
    },
    fr: {
      name: 'Répartition d’actifs',
      hook: 'Le mélange d’actifs détermine souvent le comportement long terme du portefeuille.',
      simple: 'La répartition d’actifs consiste à répartir entre actions, revenu fixe et liquidités.',
      visual: 'Des mélanges différents créent des compromis différents entre croissance et stabilité.',
      realLife: 'Un objectif immobilier proche exige souvent plus de stabilité qu’un objectif retraite lointain.',
      myth: 'Idée fausse : le choix des titres compte plus que la répartition à long terme.',
      oneThing: 'Alignez d’abord la répartition avec l’horizon de l’objectif, puis choisissez les placements.',
      whyNow: 'Une bonne répartition réduit le risque de vendre au mauvais moment.',
      nextMove: 'Associez un horizon à chaque objectif puis choisissez un niveau de risque adapté.',
      checklist: [
        'Séparez les objectifs court terme et long terme avant d’investir.',
        'Révisez la répartition chaque année ou après un changement majeur.',
      ],
    },
  },
  'tax-efficiency': {
    en: {
      name: 'Tax Efficiency',
      hook: 'Where you hold assets can change net outcomes.',
      simple: 'Tax efficiency means structuring contributions and holdings to improve after-tax results.',
      visual: 'Two portfolios with the same gross return can end with different net wealth after taxes.',
      realLife: 'Using registered accounts strategically may reduce long-term tax drag on compounding.',
      myth: 'Misconception: Taxes only matter at withdrawal or when you sell.',
      oneThing: 'After-tax return is what funds your real goals, not pre-tax return.',
      whyNow: 'Ignoring account placement can quietly reduce long-term compounding.',
      nextMove: 'Review which account each contribution is going to and whether that still fits your goals.',
      checklist: [
        'Confirm available registered room before annual contributions.',
        'Review account placement whenever income or goal timing changes.',
      ],
    },
    fr: {
      name: 'Efficacité fiscale',
      hook: 'Le choix du compte peut changer le résultat net.',
      simple: 'L’efficacité fiscale consiste à structurer cotisations et placements pour améliorer le résultat après impôt.',
      visual: 'Deux portefeuilles au même rendement brut peuvent produire un patrimoine net différent.',
      realLife: 'Une utilisation stratégique des comptes enregistrés peut réduire la friction fiscale à long terme.',
      myth: 'Idée fausse : les impôts comptent seulement au retrait ou à la vente.',
      oneThing: 'Ce qui finance vos objectifs est le rendement après impôt.',
      whyNow: 'Ignorer le placement par compte peut réduire progressivement la capitalisation.',
      nextMove: 'Vérifiez dans quel compte vont vos cotisations et si cela correspond toujours à vos objectifs.',
      checklist: [
        'Confirmez les droits de cotisation enregistrés avant la contribution annuelle.',
        'Révisez la répartition par compte quand revenu ou horizon change.',
      ],
    },
  },
  'registered-vs-non': {
    en: {
      name: 'Registered vs Non-Registered Accounts',
      hook: 'Different account types trade flexibility, limits, and tax treatment.',
      simple: 'Registered accounts have tax and contribution rules; non-registered accounts are more flexible but taxed differently.',
      visual: 'Each account type changes contribution limits, withdrawal rules, and tax timing.',
      realLife: 'Many households combine TFSA, RRSP, and non-registered accounts for different goals.',
      myth: 'Misconception: One account type is always best for everyone.',
      oneThing: 'Choose account type based on goal, timeline, and tax context, not habit.',
      whyNow: 'Using the wrong account for a goal can create avoidable tax or liquidity friction.',
      nextMove: 'Map each active goal to the account type that best matches flexibility and tax needs.',
      checklist: [
        'List your top 3 goals and required access timeline.',
        'Match each goal to a primary and backup account type.',
      ],
    },
    fr: {
      name: 'Comptes enregistrés et non enregistrés',
      hook: 'Chaque type de compte implique des compromis de flexibilité et de fiscalité.',
      simple: 'Les comptes enregistrés ont des règles de cotisation et fiscales; les non enregistrés sont plus flexibles mais imposés différemment.',
      visual: 'Le type de compte change les plafonds, retraits et le moment de l’imposition.',
      realLife: 'Beaucoup de ménages combinent CELI, REER et compte non enregistré selon les objectifs.',
      myth: 'Idée fausse : un seul type de compte est toujours optimal pour tout le monde.',
      oneThing: 'Choisissez le compte selon l’objectif, l’horizon et le contexte fiscal.',
      whyNow: 'Un mauvais choix de compte peut créer une friction fiscale ou de liquidité évitable.',
      nextMove: 'Associez chaque objectif actif au type de compte le plus cohérent.',
      checklist: [
        'Listez vos 3 objectifs principaux et leur horizon d’accès.',
        'Associez à chaque objectif un compte principal et un compte de secours.',
      ],
    },
  },
  dca: {
    en: {
      name: 'Dollar-Cost Averaging',
      hook: 'Regular investing reduces all-in timing pressure.',
      simple: 'Dollar-cost averaging means investing a fixed amount on a regular schedule.',
      visual: 'You buy more units when prices are lower and fewer when prices are higher.',
      realLife: 'Automated monthly investing can reduce emotional market timing decisions.',
      myth: 'Misconception: Dollar-cost averaging guarantees higher returns in all markets.',
      oneThing: 'Consistency is the core benefit, not a guaranteed outperformance.',
      whyNow: 'A repeatable contribution process often matters more than trying to predict short-term moves.',
      nextMove: 'Set one fixed monthly date and amount for contributions.',
      checklist: [
        'Automate contribution date and amount.',
        'Avoid changing schedule based on short-term headlines.',
      ],
    },
    fr: {
      name: 'Moyenne d’achat périodique',
      hook: 'Investir régulièrement réduit la pression du timing.',
      simple: 'La moyenne d’achat périodique consiste à investir un montant fixe à intervalles réguliers.',
      visual: 'Vous achetez plus d’unités quand les prix sont bas et moins quand ils sont élevés.',
      realLife: 'Un investissement mensuel automatisé réduit souvent les décisions émotionnelles.',
      myth: 'Idée fausse : cette méthode garantit un rendement supérieur dans tous les marchés.',
      oneThing: 'Le principal avantage est la constance, pas la garantie de surperformance.',
      whyNow: 'Un processus répétable est souvent plus utile que prédire les mouvements de court terme.',
      nextMove: 'Définissez une date et un montant mensuels fixes pour cotiser.',
      checklist: [
        'Automatisez la date et le montant de contribution.',
        'Évitez de modifier le rythme selon les manchettes de court terme.',
      ],
    },
  },
};

const CONCEPT_DEEP_DIVE = {
  compound: {
    en: {
      guide: [
        'Compounding is not magic, it is math plus consistency. The first years can look slow, which is why many people quit too early. Most of the visible acceleration happens later, after your contribution base and accumulated gains are larger.',
        'In practice, compounding rewards behavior more than prediction. People who automate contributions and keep investing through market noise usually capture more of the effect than people trying to optimize every short-term move.',
      ],
      example: {
        title: 'Example: steady monthly investing over 10 years',
        setup: 'You invest 300 per month for 10 years (total contributions: 36,000).',
        outcome: 'At 6% annual return, the portfolio grows to about 49,000, so growth adds roughly 13,000 on top of contributions.',
        takeaway: 'The key lesson is not the exact number. The lesson is that time + repetition creates the extra layer of growth.',
      },
      chart: {
        title: 'Portfolio value growth over time',
        points: [
          { label: 'Year 1', value: 3600 },
          { label: 'Year 3', value: 11700 },
          { label: 'Year 5', value: 20900 },
          { label: 'Year 10', value: 49200 },
        ],
      },
    },
    fr: {
      guide: [
        'La capitalisation n’est pas de la magie: c’est des mathématiques et de la constance. Les premières années semblent souvent lentes, ce qui pousse plusieurs personnes à abandonner trop tôt. L’accélération visible arrive surtout plus tard.',
        'Dans la vraie vie, la capitalisation récompense davantage le comportement que la prédiction. Les personnes qui cotisent automatiquement et restent investies captent généralement mieux cet effet.',
      ],
      example: {
        title: 'Exemple: cotisations mensuelles sur 10 ans',
        setup: 'Vous investissez 300 par mois pendant 10 ans (cotisations totales: 36 000).',
        outcome: 'Avec un rendement annuel de 6 %, le portefeuille atteint environ 49 000, soit près de 13 000 de croissance.',
        takeaway: 'La leçon principale: le temps et la régularité créent une croissance supplémentaire importante.',
      },
      chart: {
        title: 'Croissance de la valeur du portefeuille',
        points: [
          { label: 'An 1', value: 3600 },
          { label: 'An 3', value: 11700 },
          { label: 'An 5', value: 20900 },
          { label: 'An 10', value: 49200 },
        ],
      },
    },
  },
  inflation: {
    en: {
      guide: [
        'Inflation is the silent pressure that affects all long-term plans. If your target amount ignores inflation, you can appear on track while actually moving away from your real purchasing goal.',
        'People often underestimate inflation impact because it is gradual. A simple way to stay grounded is to convert future goals into inflated dollars each year when reviewing plans.',
      ],
      example: {
        title: 'Example: retirement income target adjusted for inflation',
        setup: 'You need 4,000 per month today and retirement is 20 years away.',
        outcome: 'At 2.5% inflation, that target becomes about 6,550 per month in future dollars.',
        takeaway: 'Using today’s price level can understate required future savings by a wide margin.',
      },
      chart: {
        title: 'Monthly cost of the same lifestyle',
        points: [
          { label: 'Today', value: 4000 },
          { label: 'Year 10', value: 5120 },
          { label: 'Year 20', value: 6550 },
        ],
      },
    },
    fr: {
      guide: [
        'L’inflation est une pression discrète qui touche tous les objectifs long terme. Si votre cible ne l’inclut pas, vous pouvez sembler en avance tout en perdant du pouvoir d’achat réel.',
        'Comme l’effet est graduel, il est souvent sous-estimé. Une bonne pratique est de réviser chaque année les objectifs en dollars futurs.',
      ],
      example: {
        title: 'Exemple: objectif de revenu retraite ajusté',
        setup: 'Vous avez besoin de 4 000 par mois aujourd’hui, retraite dans 20 ans.',
        outcome: 'Avec 2,5 % d’inflation, la cible devient environ 6 550 par mois en dollars futurs.',
        takeaway: 'Raisonner en dollars d’aujourd’hui peut sous-estimer fortement l’épargne nécessaire.',
      },
      chart: {
        title: 'Coût mensuel du même mode de vie',
        points: [
          { label: 'Aujourd’hui', value: 4000 },
          { label: 'An 10', value: 5120 },
          { label: 'An 20', value: 6550 },
        ],
      },
    },
  },
  dca: {
    en: {
      guide: [
        'Dollar-cost averaging is mainly a discipline tool. It reduces decision fatigue by converting investing into a schedule instead of a prediction exercise.',
        'It does not guarantee the best return in every market, but it often improves consistency and lowers emotional errors like waiting forever for the perfect entry point.',
      ],
      example: {
        title: 'Example: same yearly amount, different timing behavior',
        setup: 'Investor A contributes 500 each month. Investor B waits for “better timing” and contributes only 4 months of the year.',
        outcome: 'After one year, Investor A contributed 6,000 while Investor B contributed 2,000 and missed market participation most months.',
        takeaway: 'The biggest risk is often not market volatility. It is inconsistent participation.',
      },
      chart: {
        title: 'Contribution consistency over 12 months',
        points: [
          { label: 'Consistent plan', value: 6000 },
          { label: 'Timing-driven plan', value: 2000 },
        ],
      },
    },
    fr: {
      guide: [
        'La moyenne d’achat périodique est surtout un outil de discipline. Elle réduit la fatigue de décision en transformant l’investissement en routine.',
        'Elle ne garantit pas le meilleur rendement chaque année, mais elle améliore souvent la constance et réduit les erreurs émotionnelles.',
      ],
      example: {
        title: 'Exemple: même montant annuel, comportements différents',
        setup: 'Investisseur A cotise 500 chaque mois. Investisseur B attend le “bon moment” et cotise seulement 4 mois.',
        outcome: 'Après un an, A a cotisé 6 000 alors que B a cotisé 2 000 et est resté souvent hors marché.',
        takeaway: 'Le plus grand risque est souvent l’irrégularité, pas seulement la volatilité.',
      },
      chart: {
        title: 'Régularité des cotisations sur 12 mois',
        points: [
          { label: 'Plan régulier', value: 6000 },
          { label: 'Plan timing', value: 2000 },
        ],
      },
    },
  },
  'risk-return': {
    en: {
      guide: [
        'Risk and return should be calibrated to behavior, not only to expected return math. A plan that looks efficient on paper can fail if normal market volatility causes you to panic and exit.',
        'The right risk level is the one you can hold through bad periods while still funding your goals. Stability and sleep quality are valid planning constraints.',
      ],
      example: {
        title: 'Example: choosing a risk level you can stay with',
        setup: 'Portfolio A may drop 35% in a severe year. Portfolio B may drop 18% in the same year.',
        outcome: 'If Portfolio A causes panic selling, the “higher expected return” never materializes in practice.',
        takeaway: 'A slightly lower-return plan that you can consistently hold often wins in real life.',
      },
      chart: {
        title: 'Potential drawdown in a severe downturn',
        points: [
          { label: 'High-risk mix', value: 35 },
          { label: 'Balanced mix', value: 18 },
        ],
      },
    },
    fr: {
      guide: [
        'Le couple risque-rendement doit être ajusté à votre comportement réel, pas seulement à la théorie. Un plan performant sur papier peut échouer si la volatilité vous pousse à sortir du marché.',
        'Le bon niveau de risque est celui que vous pouvez conserver pendant les baisses tout en avançant vers vos objectifs.',
      ],
      example: {
        title: 'Exemple: choisir un risque que vous pouvez conserver',
        setup: 'Portefeuille A peut baisser de 35 % dans une mauvaise année. Portefeuille B peut baisser de 18 %.',
        outcome: 'Si A entraîne une vente panique, son avantage théorique disparaît en pratique.',
        takeaway: 'Un plan un peu moins agressif mais tenu avec constance gagne souvent à long terme.',
      },
      chart: {
        title: 'Baisse potentielle en période difficile',
        points: [
          { label: 'Profil élevé', value: 35 },
          { label: 'Profil équilibré', value: 18 },
        ],
      },
    },
  },
  diversification: {
    en: {
      guide: [
        'Diversification is about reducing dependence on one outcome. You are not trying to maximize every winner; you are trying to avoid one mistake destroying the whole plan.',
        'Real diversification means different risk drivers. Holding many assets that all move together can still be concentrated risk.',
      ],
      example: {
        title: 'Example: single stock vs diversified mix',
        setup: 'Investor A has 70% in one stock. Investor B caps each position under 10% across sectors.',
        outcome: 'If that one stock falls 40%, Investor A loses 28% from one name alone, while Investor B impact is much smaller.',
        takeaway: 'Position sizing matters as much as idea quality.',
      },
      chart: {
        title: 'Impact of one position falling 40%',
        points: [
          { label: 'Concentrated portfolio', value: 28 },
          { label: 'Diversified portfolio', value: 4 },
        ],
      },
    },
    fr: {
      guide: [
        'Diversifier, c’est réduire la dépendance à un seul résultat. Le but n’est pas d’attraper chaque gagnant, mais d’éviter qu’une erreur casse tout le plan.',
        'Une vraie diversification repose sur des moteurs de risque différents. Beaucoup de lignes qui bougent pareil restent un risque concentré.',
      ],
      example: {
        title: 'Exemple: action unique vs portefeuille diversifié',
        setup: 'Investisseur A met 70 % sur une action. Investisseur B limite chaque position à moins de 10 %.',
        outcome: 'Si cette action baisse de 40 %, A perd 28 % juste sur une ligne, alors que l’impact est bien plus faible pour B.',
        takeaway: 'La taille des positions compte autant que l’idée d’investissement.',
      },
      chart: {
        title: 'Impact d’une position qui baisse de 40 %',
        points: [
          { label: 'Portefeuille concentré', value: 28 },
          { label: 'Portefeuille diversifié', value: 4 },
        ],
      },
    },
  },
  allocation: {
    en: {
      guide: [
        'Asset allocation is the structure layer of your plan. It sets how much short-term volatility you are likely to experience and how much long-term growth potential you target.',
        'Most people should align allocation to goal horizon, not to what performed best recently. Short-horizon goals generally need stability; long-horizon goals can absorb more volatility.',
      ],
      example: {
        title: 'Example: matching allocation to goal horizon',
        setup: 'Goal A is a home purchase in 3 years. Goal B is retirement in 25 years.',
        outcome: 'Goal A often needs a more stable mix, while Goal B can use a growth-oriented mix with higher equity exposure.',
        takeaway: 'One allocation is rarely optimal for every goal at once.',
      },
      chart: {
        title: 'Illustrative equity exposure by goal horizon',
        points: [
          { label: '3-year goal', value: 35 },
          { label: '10-year goal', value: 60 },
          { label: '25-year goal', value: 80 },
        ],
      },
    },
    fr: {
      guide: [
        'La répartition d’actifs est la structure de votre plan. Elle détermine une grande partie de la volatilité de court terme et du potentiel de croissance de long terme.',
        'La répartition doit surtout suivre l’horizon de l’objectif, pas la performance récente. Les objectifs proches demandent souvent plus de stabilité.',
      ],
      example: {
        title: 'Exemple: adapter la répartition à l’horizon',
        setup: 'Objectif A: achat immobilier dans 3 ans. Objectif B: retraite dans 25 ans.',
        outcome: 'A nécessite souvent une allocation plus stable; B peut accepter plus d’actions pour la croissance.',
        takeaway: 'Une seule allocation est rarement idéale pour tous les objectifs.',
      },
      chart: {
        title: 'Exposition actions selon horizon (illustratif)',
        points: [
          { label: 'Objectif 3 ans', value: 35 },
          { label: 'Objectif 10 ans', value: 60 },
          { label: 'Objectif 25 ans', value: 80 },
        ],
      },
    },
  },
  'tax-efficiency': {
    en: {
      guide: [
        'Tax efficiency is about what you keep, not just what you earn. Two portfolios with similar pre-tax return can produce meaningfully different outcomes after taxes and fees.',
        'Account placement decisions can compound over long periods. Small annual tax drag differences become large when repeated for many years.',
      ],
      example: {
        title: 'Example: same gross return, different net result',
        setup: 'Portfolio A and B both earn 6% gross. A loses 1.2% annual tax drag; B loses 0.6%.',
        outcome: 'Over long periods, B can retain substantially more wealth even with the same gross return.',
        takeaway: 'After-tax return is the number that pays your goals.',
      },
      chart: {
        title: 'Illustrative annual return kept after tax drag',
        points: [
          { label: 'Higher tax drag', value: 4.8 },
          { label: 'Lower tax drag', value: 5.4 },
        ],
      },
    },
    fr: {
      guide: [
        'L’efficacité fiscale concerne ce que vous gardez, pas seulement ce que vous gagnez. Deux portefeuilles au même rendement brut peuvent donner des résultats nets très différents.',
        'Le placement des actifs par type de compte crée un effet cumulatif. De petites différences annuelles deviennent importantes sur la durée.',
      ],
      example: {
        title: 'Exemple: même rendement brut, résultat net différent',
        setup: 'Portefeuille A et B font 6 % brut. A perd 1,2 % en frottement fiscal annuel; B perd 0,6 %.',
        outcome: 'Sur longue période, B peut conserver nettement plus de capital.',
        takeaway: 'C’est le rendement après impôt qui finance vos objectifs.',
      },
      chart: {
        title: 'Rendement annuel conservé après impôt (illustratif)',
        points: [
          { label: 'Frottement élevé', value: 4.8 },
          { label: 'Frottement réduit', value: 5.4 },
        ],
      },
    },
  },
  'registered-vs-non': {
    en: {
      guide: [
        'Account type is a strategy decision, not a paperwork detail. Different accounts change tax timing, contribution limits, and withdrawal flexibility.',
        'Most households need a combination, not a single account. Matching each goal to the right account type can reduce both tax friction and liquidity stress.',
      ],
      example: {
        title: 'Example: mapping goals to account types',
        setup: 'Emergency reserve, retirement target, and medium-term house fund are active at the same time.',
        outcome: 'A blended structure (for example TFSA + RRSP + non-registered when needed) can balance flexibility and tax efficiency better than one-account-only plans.',
        takeaway: 'Choose account type by goal function, not by habit.',
      },
      chart: {
        title: 'Illustrative account role split',
        points: [
          { label: 'Flexible access bucket', value: 35 },
          { label: 'Long-term tax-advantaged bucket', value: 50 },
          { label: 'Overflow/flex bucket', value: 15 },
        ],
      },
    },
    fr: {
      guide: [
        'Le type de compte est une décision stratégique, pas un détail administratif. Chaque compte modifie l’impôt, les plafonds et la flexibilité de retrait.',
        'La plupart des ménages ont intérêt à combiner plusieurs comptes plutôt qu’à n’en utiliser qu’un seul.',
      ],
      example: {
        title: 'Exemple: associer objectifs et types de comptes',
        setup: 'Fonds d’urgence, retraite et projet immobilier coexistent.',
        outcome: 'Une structure mixte (ex. CELI + REER + non enregistré au besoin) équilibre mieux flexibilité et efficacité fiscale.',
        takeaway: 'Choisissez le compte selon la fonction de l’objectif.',
      },
      chart: {
        title: 'Répartition illustrative des rôles de comptes',
        points: [
          { label: 'Poche flexible', value: 35 },
          { label: 'Poche long terme avantage fiscal', value: 50 },
          { label: 'Poche complémentaire', value: 15 },
        ],
      },
    },
  },
};

const localize = (item, lang) => item?.[lang] || item?.en || item;

const ConceptsLibrary = () => {
  const { lang } = useLanguage();
  const c = UI_COPY[lang] || UI_COPY.en;
  const [activeTrackId, setActiveTrackId] = useState(null);
  const [activeConceptId, setActiveConceptId] = useState(null);
  const conceptStepRef = useRef(null);
  const detailStepRef = useRef(null);

  const localizedTracks = useMemo(
    () => LEARNING_TRACKS.map((track) => ({ ...track, ...localize(track, lang) })),
    [lang],
  );

  const conceptCatalog = useMemo(
    () => Object.entries(CONCEPT_LIBRARY).reduce((acc, [id, concept]) => {
      acc[id] = { id, ...localize(concept, lang), deepDive: localize(CONCEPT_DEEP_DIVE[id], lang) };
      return acc;
    }, {}),
    [lang],
  );

  const activeTrack = localizedTracks.find((track) => track.id === activeTrackId) || null;
  const visibleConcepts = activeTrack
    ? activeTrack.conceptIds.map((id) => conceptCatalog[id]).filter(Boolean)
    : [];
  const activeConcept = activeConceptId
    ? visibleConcepts.find((concept) => concept.id === activeConceptId) || null
    : null;

  useEffect(() => {
    if (!activeTrack || !activeConceptId) {
      return;
    }
    if (!activeTrack.conceptIds.includes(activeConceptId)) {
      setActiveConceptId(null);
    }
  }, [activeTrack, activeConceptId]);

  const scrollToStep = (sectionRef) => {
    if (!sectionRef?.current || typeof window === 'undefined') {
      return;
    }
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    sectionRef.current.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  useEffect(() => {
    if (!activeTrack || typeof window === 'undefined') {
      return;
    }
    const frame = window.requestAnimationFrame(() => scrollToStep(conceptStepRef));
    return () => window.cancelAnimationFrame(frame);
  }, [activeTrack]);

  useEffect(() => {
    if (!activeConcept || typeof window === 'undefined') {
      return;
    }
    const frame = window.requestAnimationFrame(() => scrollToStep(detailStepRef));
    return () => window.cancelAnimationFrame(frame);
  }, [activeConcept]);

  const handleTrackSelect = (trackId) => {
    setActiveTrackId(trackId);
    setActiveConceptId(null);
  };

  const resetToStepOne = () => {
    setActiveTrackId(null);
    setActiveConceptId(null);
  };

  return (
    <div className="concepts-root">
      <section className="concepts-hero dashboard-glass">
        <h2>{c.title}</h2>
        <p>{c.subtitle}</p>
      </section>

      <ComplianceNotice compact />

      <section className="concepts-track-panel dashboard-glass">
        <div>
          <span className="concepts-step-pill">{c.stepTrack}</span>
          <h3>{c.trackTitle}</h3>
          <p>{c.trackSubtitle}</p>
        </div>
        <div className="concepts-track-grid" role="tablist" aria-label={c.labels.trackList}>
          {localizedTracks.map((track) => {
            const Icon = track.icon;
            const isActive = track.id === activeTrackId;
            return (
              <button
                key={track.id}
                type="button"
                className={`concepts-track-card ${isActive ? 'active' : ''}`}
                onClick={() => handleTrackSelect(track.id)}
              >
                <span className="concepts-track-icon"><Icon /></span>
                <span>
                  <strong>{track.title}</strong>
                  <small>{track.description}</small>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="concepts-learning-flow">
        {activeTrack && (
          <aside ref={conceptStepRef} className="concepts-concept-panel dashboard-glass concepts-flow-reveal">
            <span className="concepts-step-pill">{c.stepConcept}</span>
            <h3>{c.labels.conceptList}</h3>
            <p>{c.labels.conceptPrompt}</p>
            <div className="concepts-step-actions">
              <button type="button" className="concepts-step-action-btn" onClick={resetToStepOne}>
                {c.backToStep1}
              </button>
            </div>
            <div className="concepts-selected-track">
              <small>{c.labels.selectedTrack}</small>
              <strong>{activeTrack.title}</strong>
              <span>{activeTrack.description}</span>
            </div>
            <div className="concepts-topic-grid" role="tablist" aria-label={c.labels.conceptList}>
              {visibleConcepts.map((concept) => {
                const isActive = concept.id === activeConceptId;
                return (
                  <button
                    key={concept.id}
                    type="button"
                    className={`concepts-topic-btn ${isActive ? 'active' : ''}`}
                    onClick={() => setActiveConceptId(concept.id)}
                  >
                    <strong>{concept.name}</strong>
                    <small>{concept.hook}</small>
                  </button>
                );
              })}
            </div>
          </aside>
        )}

        {activeConcept && (
          <article
            ref={detailStepRef}
            className="concepts-detail-panel dashboard-glass concepts-flow-reveal"
            role="tabpanel"
            aria-label={activeConcept.name}
          >
            <span className="concepts-step-pill">{c.stepDetails}</span>
            <header className="concepts-detail-head">
              <h3>{c.detailTitle}: {activeConcept.name}</h3>
              <p>{c.detailSubtitle}</p>
            </header>

            <div className="concepts-step-actions">
              <button type="button" className="concepts-step-action-btn" onClick={() => setActiveConceptId(null)}>
                {c.backToStep2}
              </button>
              <button type="button" className="concepts-step-action-btn" onClick={resetToStepOne}>
                {c.changePath}
              </button>
              <button type="button" className="concepts-step-action-btn" onClick={() => setActiveConceptId(null)}>
                {c.changeConcept}
              </button>
            </div>

            <section className="concepts-quick-summary-panel">
              <h4>{c.quickSummaryTitle}</h4>
              <p className="concepts-quick-summary-subtitle">{c.quickSummarySubtitle}</p>
              <div className="concepts-quick-summary-grid">
                <article className="concepts-quick-summary-card">
                  <small>{c.quickOneThing}</small>
                  <p>{activeConcept.oneThing}</p>
                </article>
                <article className="concepts-quick-summary-card">
                  <small>{c.quickWhyNow}</small>
                  <p>{activeConcept.whyNow}</p>
                </article>
                <article className="concepts-quick-summary-card warning">
                  <small>{c.quickTrap}</small>
                  <p>{activeConcept.myth}</p>
                </article>
              </div>
            </section>

            <section className="concepts-practical-guide">
              <h4>{c.labels.practicalGuide}</h4>
              <div className="concepts-practical-guide-body">
                {activeConcept.deepDive?.guide?.map((item) => (
                  <p key={`${activeConcept.id}-guide-${item}`}>{item}</p>
                ))}
              </div>
            </section>

            <section className="concepts-example-panel">
              <h4>{c.labels.concreteExample}</h4>
              <div className="concepts-example-content">
                <p className="concepts-example-title">{activeConcept.deepDive?.example?.title}</p>
                <p><strong>{lang === 'fr' ? 'Contexte: ' : 'Setup: '}</strong>{activeConcept.deepDive?.example?.setup}</p>
                <p><strong>{lang === 'fr' ? 'Résultat: ' : 'Outcome: '}</strong>{activeConcept.deepDive?.example?.outcome}</p>
                <p><strong>{lang === 'fr' ? 'À retenir: ' : 'Takeaway: '}</strong>{activeConcept.deepDive?.example?.takeaway}</p>
              </div>
            </section>

            <section className="concepts-chart-panel">
              <h4>{c.labels.conceptChart}</h4>
              <p className="concepts-chart-title">{activeConcept.deepDive?.chart?.title}</p>
              <div className="concepts-chart-bars">
                {(() => {
                  const points = activeConcept.deepDive?.chart?.points || [];
                  const maxValue = points.length ? Math.max(...points.map((point) => Number(point.value) || 0)) : 1;
                  return points.map((point) => {
                    const numericValue = Number(point.value) || 0;
                    const widthPct = Math.max(6, (numericValue / (maxValue || 1)) * 100);
                    return (
                      <div key={`${activeConcept.id}-chart-${point.label}`} className="concepts-chart-row">
                        <span className="concepts-chart-label">{point.label}</span>
                        <div className="concepts-chart-track">
                          <div className="concepts-chart-fill" style={{ width: `${widthPct}%` }} />
                        </div>
                        <span className="concepts-chart-value">{point.value}</span>
                      </div>
                    );
                  });
                })()}
              </div>
            </section>

            <section className="concepts-content-stack">
              <section className="concepts-detail-section no-margin">
                <h4>{c.labels.simple}</h4>
                <p>{activeConcept.simple}</p>
              </section>

              <section className="concepts-detail-section">
                <h4>{c.labels.visual}</h4>
                <p>{activeConcept.visual}</p>
              </section>

              <section className="concepts-detail-section">
                <h4>{c.labels.realLife}</h4>
                <p>{activeConcept.realLife}</p>
              </section>

              <section className="concepts-detail-section">
                <h4>{c.labels.nextMove}</h4>
                <p>{activeConcept.nextMove}</p>
              </section>

              <section className="concepts-detail-section">
                <h4>{c.labels.checklist}</h4>
                <ul>
                  {activeConcept.checklist.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="concepts-detail-section misconception">
                <h4>{c.labels.misconception}</h4>
                <p>{activeConcept.myth}</p>
              </section>
            </section>

            <section className="concepts-next-actions">
              <div>
                <h4>{c.nextSectionTitle}</h4>
                <p>{c.nextSectionSubtitle}</p>
              </div>
              <div className="concepts-next-links">
                <Link to="/planning/accounts" className="concepts-next-link">
                  <span>{c.nextLinks.accounts}</span>
                  <FiArrowRight />
                </Link>
                <Link to="/planning/forecast" className="concepts-next-link">
                  <span>{c.nextLinks.forecast}</span>
                  <FiArrowRight />
                </Link>
              </div>
            </section>
          </article>
        )}
      </section>
    </div>
  );
};

export default ConceptsLibrary;
